import React from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import type { AnalyticsTab } from "@/components/pages/analytics/config/analyticsTabs";
import { Button } from "@/components/ui/button";
import { Column, DataTable } from "@/components/ui/data-table";
import { cn } from "@/lib/utils";
import { buildAdminHotelAnalyticsUrl } from "../../utils/hotelAnalyticsNavigation";
import type {
  AdminHotelMetricRow,
  AdminTabMetricColumnDef,
} from "../../types/adminHotelMetricTable";

const HEADER_CLASS =
  "text-[10px] font-medium leading-5 tracking-normal text-[#737373]";
const CELL_CLASS =
  "text-[10px] font-normal leading-[100%] tracking-normal text-[#0A0A0A]";
const DELTA_CLASS_BASE =
  "text-[10px] font-normal leading-[100%] tracking-normal";
const trendHighlightClassName = {
  positive: "text-emerald-600",
  negative: "text-red-600",
  neutral: "text-zinc-500",
};

function resolveTrendFromDelta(
  delta?: string,
): keyof typeof trendHighlightClassName {
  if (!delta) return "neutral";
  const trimmed = delta.trim();
  if (trimmed.startsWith("+")) return "positive";
  if (trimmed.startsWith("-")) return "negative";
  return "neutral";
}

function resolveDeltaTrend(
  delta?: string,
  trend?: "positive" | "negative" | "neutral",
): keyof typeof trendHighlightClassName {
  if (trend) return trend;
  return resolveTrendFromDelta(delta);
}

type AdminHotelsMetricTableProps = {
  tab: AnalyticsTab;
  columns: AdminTabMetricColumnDef[];
  rows: AdminHotelMetricRow[];
  loading: boolean;
  emptyMessage: string;
};

export const AdminHotelsMetricTable = ({
  tab,
  columns,
  rows,
  loading,
  emptyMessage,
}: AdminHotelsMetricTableProps): React.JSX.Element => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const dataColumns: Column<AdminHotelMetricRow>[] = React.useMemo(
    () => [
      {
        key: "hotelBrand",
        header: t("analytics.admin.table.hotelBrand", "Hotel Brand"),
        className: "text-left",
        sortable: true,
        render: (_, row) => (
          <span className={CELL_CLASS}>{row.hotelBrand}</span>
        ),
      },
      ...columns.map(
        (column): Column<AdminHotelMetricRow> => ({
          key: column.key,
          header: t(column.headerKey, column.headerFallback),
          className: "text-center",
          sortable: true,
          render: (_, row) => (
            <div className="flex w-full flex-col items-center">
              <span
                className={cn(CELL_CLASS, "inline-block w-full text-center")}
              >
                {row.metrics[column.key]?.value ?? "—"}
              </span>
              {row.metrics[column.key]?.delta ? (
                <span
                  className={cn(
                    DELTA_CLASS_BASE,
                    trendHighlightClassName[
                      resolveDeltaTrend(
                        row.metrics[column.key]?.delta,
                        row.metrics[column.key]?.trend,
                      )
                    ],
                  )}
                >
                  {row.metrics[column.key]?.delta}
                </span>
              ) : null}
            </div>
          ),
        }),
      ),
      {
        key: "actions",
        header: t("billing.guesteriaPartnersRevenue.actions", "Actions"),
        className: "text-center",
        render: (_, row) => (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            aria-label={t(
              "billing.guesteriaPartnersRevenue.actions",
              "Actions",
            )}
            onClick={() => {
              const targetUrl = buildAdminHotelAnalyticsUrl(
                tab,
                searchParams,
                row.id,
              );
              window.open(targetUrl, "_blank", "noopener,noreferrer");
            }}
          >
            <ExternalLink className="size-4 text-zinc-500" />
          </Button>
        ),
      },
    ],
    [columns, searchParams, t, tab],
  );

  const [sortField, setSortField] = React.useState<string | null>(null);
  const [sortDirection, setSortDirection] = React.useState<
    "asc" | "desc" | null
  >(null);

  const sortedRows = React.useMemo(() => {
    if (!sortField || !sortDirection) {
      return rows;
    }

    const direction = sortDirection === "asc" ? 1 : -1;

    return [...rows].sort((a, b) => {
      if (sortField === "hotelBrand") {
        return a.hotelBrand.localeCompare(b.hotelBrand) * direction;
      }

      const av = a.metrics[sortField]?.value ?? "";
      const bv = b.metrics[sortField]?.value ?? "";
      return av.localeCompare(bv) * direction;
    });
  }, [rows, sortDirection, sortField]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
        return;
      }
      if (sortDirection === "desc") {
        setSortField(null);
        setSortDirection(null);
        return;
      }
    }

    setSortField(field);
    setSortDirection("asc");
  };

  return (
    <div className="bg-white p-4">
      <DataTable<AdminHotelMetricRow>
        data={sortedRows}
        getRowKey={(row) => row.id}
        columns={dataColumns}
        loading={loading}
        columnHeaderClassName={HEADER_CLASS}
        emptyMessage={emptyMessage}
        withPagination={false}
        sorting={{
          field: sortField,
          direction: sortDirection,
          onSort: handleSort,
        }}
      />
    </div>
  );
};
